import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { components as clientComponents } from 'mage-client';
import { isRunningContextCypress } from 'moet-test-utils/cypress/utils';

import { showRecaptchaChallenge, setRecaptchaTokenExpired } from '@/actions/recaptcha';
import { recaptchaChallengeCompleted, recaptchaResponseToken } from '@/selectors/recaptcha';
import { FEATURES } from '@/constants';
import { getFeatureByKey } from '@/selectors/featuresConfig';

const isCypress = isRunningContextCypress(window);

/**
 * Overall wrapper for recaptcha. First uses v3 token, if v3 token is invalid,
 * then triggers v2 challenge.
 */
export const withReCaptchaProtectedCapability = cap => WrappedCoponent => {
  if (Object.keys(cap).length !== 1) {
    throw new Error('This HOC should only be used for one capability');
  }

  const capabilityPropFuncName = Object.keys(cap)[0];
  const capability = cap[capabilityPropFuncName];

  const ReCaptchaWrapper = props => {
    /**
     * store parameters passed to the capability call for when v2 challenge should be retriggered.
     */
    const [capParams, setCapParams] = useState({ params: null, onComplete: null });
    /**
     * store when an actual capability call was made. prevents unintentional
     * cap trigger from `useEffect` hook
     */
    const [capCalled, setCapCalled] = useState(false);
    /**
     * prevent multiple capability triggers from `useEffect` hook.
     */
    const [v2RequestMade, setV2RequestMade] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const {
      showRecaptchaChallenge,
      recaptchaChallengeCompleted,
      recaptchaResponseToken,
      recaptchaV3On,
      skipRecaptchaOn,
      setRecaptchaTokenExpired,
    } = props;
    const capabilityCall = props[capabilityPropFuncName];

    const onCapCall = useCallback(
      async (params, onComplete = () => {}) => {
        // TODO remove after both BE and FE are deployed
        if (!recaptchaV3On && !recaptchaChallengeCompleted) {
          setCapCalled(true);
          setCapParams({ params, onComplete });
          showRecaptchaChallenge();
          return;
        }

        const token = await (recaptchaChallengeCompleted || isCypress || skipRecaptchaOn
          ? Promise.resolve()
          : executeRecaptcha(capability.KEY));

        const { error, payload } = await capabilityCall({
          ...params,
          body: {
            ...params?.body,
            recaptchaResponse: recaptchaResponseToken,
            recaptchaResponseV3: token,
          },
        })[1];

        if (error && payload.response?.status === 403 && !recaptchaChallengeCompleted) {
          setCapCalled(true);
          setCapParams({ params, onComplete });
          // when the challenge is resolved, `useEffect` below will get triggered
          showRecaptchaChallenge();
        } else {
          setRecaptchaTokenExpired();
          await onComplete({ error, payload });
        }
      },
      [executeRecaptcha, recaptchaResponseToken, recaptchaChallengeCompleted],
    );

    useEffect(() => {
      if (capCalled && recaptchaChallengeCompleted && !v2RequestMade) {
        setCapCalled(false);
        setV2RequestMade(true);
        onCapCall(capParams.params, capParams.onComplete);
      }
    }, [onCapCall, v2RequestMade, capCalled, capParams, recaptchaChallengeCompleted]);

    const injectedProps = {
      [capabilityPropFuncName]: onCapCall,
      recaptchaReady: isCypress ? true : !!executeRecaptcha,
    };

    return <WrappedCoponent {...props} {...injectedProps} />;
  };

  ReCaptchaWrapper.propTypes = {
    recaptchaV3On: PropTypes.bool.isRequired,
    setRecaptchaTokenExpired: PropTypes.func.isRequired,
    showRecaptchaChallenge: PropTypes.func.isRequired,
    skipRecaptchaOn: PropTypes.bool.isRequired,
    recaptchaChallengeCompleted: PropTypes.bool,
    recaptchaResponseToken: PropTypes.string,
  };

  return compose(
    clientComponents.withCapabilities(cap),
    connect(
      state => ({
        recaptchaChallengeCompleted: recaptchaChallengeCompleted(state),
        recaptchaResponseToken: recaptchaResponseToken(state),
        recaptchaV3On: getFeatureByKey(FEATURES.RECAPTCHA_V3)(state),
        skipRecaptchaOn: getFeatureByKey(FEATURES.SKIP_RECAPTCHA_CHALLENGE)(state),
      }),
      { showRecaptchaChallenge, setRecaptchaTokenExpired },
    ),
  )(ReCaptchaWrapper);
};
